import React, { useState, useEffect, useCallback } from 'react';
import { Box, Snackbar, Typography, TextField } from '@mui/material';
import axios from 'axios';
import { useMsal, useAccount } from '@azure/msal-react';
import { loginRequest } from '../msalConfig';
import { useNavigate } from 'react-router-dom';
import styles from './AdminDashboard.module.css';

// Import components
import AppHeader from './AppHeader';
import Sidebar from './Sidebar';
import SearchBar from './SearchBar';
import DialogBox from './DialogBox';
import MainContent from './MainContent';

function AdminDashboard({ themeMode, setThemeMode }) {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [interviews, setInterviews] = useState([]);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [aiInstructions, setAiInstructions] = useState('');
  const [currentView, setCurrentView] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [language, setLanguage] = useState('en');
  const [adminData, setAdminData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [interviewToDelete, setInterviewToDelete] = useState(null);
  const [interviewees, setInterviewees] = useState([]);
  const [newInterviewee, setNewInterviewee] = useState({ firstName: '', lastName: '', email: '' });
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [bulkInterviewees, setBulkInterviewees] = useState('');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInterviewees, setSelectedInterviewees] = useState([]);
  const [deleteIntervieweeConfirmOpen, setDeleteIntervieweeConfirmOpen] = useState(false);
  const [intervieweeToDelete, setIntervieweeToDelete] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    interviewCount: 0,
    intervieweeCount: 0,
    recentInterviews: [],
    completedInterviews: 0,
    interviewStatusCounts: []
  });

  useEffect(() => {
    if (account) {
      const names = account.name ? account.name.split(' ') : ['', ''];
      setAdminData({
        firstName: names[0],
        lastName: names.slice(1).join(' '),
        email: account.username
      });
    }
  }, [account]);

  const fetchInterviews = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      const token = response.accessToken;
      const interviewsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/get-interviews`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setInterviews(interviewsResponse.data);
    } catch (error) {
      console.error('Error fetching interviews:', error);
      setError('Failed to fetch interviews. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [account, instance]);

  const fetchAiInstructions = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      const token = response.accessToken;
      const aiInstructionsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/get-ai-instructions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAiInstructions(aiInstructionsResponse.data.instructions);
    } catch (error) {
      console.error('Error fetching AI instructions:', error);
      setError('Failed to fetch AI instructions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [account, instance]);

  const fetchInterviewees = useCallback(async () => {
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      const token = response.accessToken;
      const intervieweesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/interviewees`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setInterviewees(intervieweesResponse.data);
    } catch (error) {
      console.error('Error fetching interviewees:', error);
    }
  }, [account, instance]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      const token = response.accessToken;
      const dashboardResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/dashboard-data`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDashboardData(dashboardResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  }, [account, instance]);

  useEffect(() => {
    if (!account) {
      instance.loginRedirect(loginRequest);
      return;
    }

    fetchInterviews();
    fetchAiInstructions();
    fetchInterviewees();
  }, [account, instance, fetchInterviews, fetchAiInstructions, fetchInterviewees]);

  useEffect(() => {
    if (currentView === 'dashboard') {
      fetchDashboardData();
    }
  }, [currentView, fetchDashboardData]);

  const updateAiInstructions = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      const token = response.accessToken;
      const updateResponse = await axios.post(`${process.env.REACT_APP_API_URL}/api/update-ai-instructions`, {
        instructions: aiInstructions
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (updateResponse.data.status === 'success') {
        setSnackbarMessage('AI instructions updated successfully!');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error updating AI instructions:', error);
      setError('Failed to update AI instructions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInterviewClick = (interview) => {
    setSelectedInterview(interview);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + "/admin-login"
      });
    } catch (e) {
      console.error("Logout failed", e);
      navigate('/admin-login');
    }
  };

  const handleLanguageChange = (event) => {
    setLanguage(event.target.value);
  };

  const handleDeleteClick = (interview) => {
    setInterviewToDelete(interview);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (interviewToDelete) {
      await handleDeleteInterview(interviewToDelete.id);
      setDeleteConfirmOpen(false);
      setInterviewToDelete(null);
      setSelectedInterview(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setInterviewToDelete(null);
  };

  const handleDeleteInterview = async (interviewId) => {
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      const token = response.accessToken;
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/delete-interview/${interviewId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setInterviews(interviews.filter(interview => interview.id !== interviewId));
      setSnackbarMessage('Interview deleted successfully.');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting interview:', error);
      setError('Failed to delete interview. Please try again.');
      setSnackbarMessage('Failed to delete interview. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleShareInterview = async (interview) => {
    try {
      const response = await instance.acquireTokenSilent({ ...loginRequest, account: account });
      const token = response.accessToken;
      const reportResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/generate-interview-report/${interview.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const reportUrl = reportResponse.data.report_url;
      const shareLink = `mailto:?subject=Interview Report&body=Here is the interview report for ${interview.user_id}. You can view it at: ${reportUrl}`;
      window.open(shareLink, '_blank');
    } catch (error) {
      console.error('Error generating report:', error);
      setSnackbarMessage('Failed to generate report. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handlePrintInterview = () => {
    if (!selectedInterview) {
      console.error('No interview selected for printing');
      setSnackbarMessage('Please select an interview to print.');
      setSnackbarOpen(true);
      return;
    }

    const printContent = `
      <html>
      <head>
        <title>Interview Report for ${selectedInterview.first_name} ${selectedInterview.last_name}</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; }
          h1 { color: #333; }
          h2 { color: #666; }
          .message { margin-bottom: 10px; }
          .user { color: #0066cc; }
          .assistant { color: #006600; }
        </style>
      </head>
      <body>
        <h1>Interview Report for ${selectedInterview.first_name} ${selectedInterview.last_name}</h1>
        <p><strong>Interview ID:</strong> ${selectedInterview.id}</p>
        <p><strong>User ID:</strong> ${selectedInterview.user_id}</p>
        <p><strong>Timestamp:</strong> ${selectedInterview.timestamp}</p>
        <h2>Messages:</h2>
        ${selectedInterview.messages ? selectedInterview.messages.map(message => `
          <div class="message">
            <strong class="${message.role}">${message.role}:</strong> ${message.content}
          </div>
        `).join('') : ''}
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleAddInterviewee = async () => {
    if (!newInterviewee.firstName.trim() || !newInterviewee.lastName.trim() || !newInterviewee.email.trim()) {
      setSnackbarMessage("All fields are required.");
      setSnackbarOpen(true);
      return;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      const token = response.accessToken;
      const newIntervieweeWithId = { ...newInterviewee, id: Date.now().toString() };
      await axios.post(`${process.env.REACT_APP_API_URL}/api/interviewees`, newIntervieweeWithId, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setInterviewees([...interviewees, newIntervieweeWithId]);
      setNewInterviewee({ firstName: '', lastName: '', email: '' });
      setSnackbarMessage("Interviewee added successfully.");
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error adding interviewee:', error);
      setSnackbarMessage("Failed to add interviewee. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const handleBulkAddOpen = () => {
    setBulkAddOpen(true);
  };

  const handleBulkAddClose = () => {
    setBulkAddOpen(false);
    setBulkInterviewees('');
  };

  const handleBulkAddInterviewees = async () => {
    const intervieweesToAdd = bulkInterviewees.split('\n').map(interviewee => {
      const parts = interviewee.split(',').map(item => item.trim());
      if (parts.length !== 3) {
        return null;
      }
      const [firstName, lastName, email] = parts;
      return { firstName, lastName, email };
    }).filter(interviewee => interviewee !== null);

    if (intervieweesToAdd.length === 0) {
      setSnackbarMessage("Please provide valid interviewee data.");
      setSnackbarOpen(true);
      return;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      const token = response.accessToken;

      await axios.post(`${process.env.REACT_APP_API_URL}/api/bulk-interviewees`, intervieweesToAdd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      setSnackbarMessage('Interviewees added successfully.');
      setSnackbarOpen(true);
      setBulkInterviewees('');
      fetchInterviewees();
    } catch (error) {
      console.error('Error adding interviewees:', error);
      setSnackbarMessage('Failed to add interviewees. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteInterviewee = (id) => {
    const interviewee = interviewees.find(i => i.id === id);
    if (interviewee) {
      setIntervieweeToDelete(interviewee);
      setDeleteIntervieweeConfirmOpen(true);
    }
  };

  const handleDeleteIntervieweeConfirm = async () => {
    if (intervieweeToDelete) {
      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: account
        });
        const token = response.accessToken;
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/interviewees/${intervieweeToDelete.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setInterviewees(interviewees.filter(interviewee => interviewee.id !== intervieweeToDelete.id));
        setSnackbarMessage('Interviewee deleted successfully.');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Error deleting interviewee:', error);
        setSnackbarMessage('Failed to delete interviewee. Please try again.');
        setSnackbarOpen(true);
      } finally {
        setDeleteIntervieweeConfirmOpen(false);
        setIntervieweeToDelete(null);
      }
    }
  };

  const handleDeleteIntervieweeCancel = () => {
    setDeleteIntervieweeConfirmOpen(false);
    setIntervieweeToDelete(null);
  };

  const handleDeleteSelectedInterviewees = () => {
    setDeleteIntervieweeConfirmOpen(true);
  };

  const handleIntervieweeSelect = (id) => {
    setSelectedInterviewees(prevSelected =>
      prevSelected.includes(id) ? prevSelected.filter(i => i !== id) : [...prevSelected, id]
    );
  };

  const handleReviewStatusChange = async (event, interview) => {
    const newStatus = event.target.checked ? 'reviewed' : 'unreviewed';
    const newReviewDate = event.target.checked ? new Date().toISOString() : null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      const token = response.accessToken;
      await axios.post(`${process.env.REACT_APP_API_URL}/api/update-interview`, {
        id: interview.id,
        reviewStatus: newStatus,
        reviewDate: newReviewDate
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setInterviews(interviews.map(i => i.id === interview.id ? { ...i, reviewStatus: newStatus, reviewDate: newReviewDate } : i));
      setSelectedInterview({ ...interview, reviewStatus: newStatus, reviewDate: newReviewDate });
    } catch (error) {
      console.error('Error updating interview:', error);
    }
  };

  const handleNotesChange = async (event, interview) => {
    const newNotes = event.target.value;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      const token = response.accessToken;
      await axios.post(`${process.env.REACT_APP_API_URL}/api/update-interview`, {
        id: interview.id,
        notes: newNotes
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setInterviews(interviews.map(i => i.id === interview.id ? { ...i, notes: newNotes } : i));
      setSelectedInterview({ ...interview, notes: newNotes });
    } catch (error) {
      console.error('Error updating interview:', error);
    }
  };

  const handleReviewFilterChange = (event) => {
    setReviewFilter(event.target.value);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      if (currentView === 'interviews') {
        fetchInterviews();
      } else if (currentView === 'interviewees') {
        fetchInterviewees();
      }
      return;
    }
  
    setIsLoading(true);
    setError('');
    try {
      const response = await instance.acquireTokenSilent({ ...loginRequest, account: account });
      const token = response.accessToken;
      let searchResults;
      if (currentView === 'interviews') {
        const searchResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/search-interviews?q=${encodeURIComponent(searchTerm)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        searchResults = searchResponse.data;
        setInterviews(searchResults);
      } else if (currentView === 'interviewees') {
        const searchResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/search-interviewees?q=${encodeURIComponent(searchTerm)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        searchResults = searchResponse.data;
        setInterviewees(searchResults);
      }
    } catch (error) {
      console.error('Error searching:', error);
      setError('Failed to search. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredInterviews = interviews.filter(interview => reviewFilter === 'all' || interview.reviewStatus === reviewFilter);

  const resetView = (view) => {
    setCurrentView(view);
    setSearchTerm('');
    if (view === 'interviews') {
      fetchInterviews();
    } else if (view === 'interviewees') {
      fetchInterviewees();
    }
  };

  const pageVariants = {
    initial: { opacity: 0, x: "-100vw" },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: "100vw" }
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.5
  };

  return (
    <Box className={styles.dashboardContainer}>
      <AppHeader
        handleMenu={handleMenu}
        anchorEl={anchorEl}
        handleClose={handleClose}
        setCurrentView={setCurrentView}
        handleSignOut={handleSignOut}
        toggleSidebar={() => setCollapsed(!collapsed)}
      />
      <Sidebar
        collapsed={collapsed}
        resetView={resetView}
      />
      <Box component="main" className={styles.mainContent}>
        {(currentView === 'interviews' || currentView === 'interviewees') && (
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            handleSearch={handleSearch}
            currentView={currentView}
          />
        )}
        <MainContent
          currentView={currentView}
          isLoading={isLoading}
          error={error}
          pageVariants={pageVariants}
          pageTransition={pageTransition}
          selectedInterview={selectedInterview}
          setSelectedInterview={setSelectedInterview}
          handleNotesChange={handleNotesChange}
          handleReviewStatusChange={handleReviewStatusChange}
          handlePrintInterview={handlePrintInterview}
          handleShareInterview={handleShareInterview}
          handleDeleteClick={handleDeleteClick}
          filteredInterviews={filteredInterviews}
          handleInterviewClick={handleInterviewClick}
          reviewFilter={reviewFilter}
          handleReviewFilterChange={handleReviewFilterChange}
          newInterviewee={newInterviewee}
          setNewInterviewee={setNewInterviewee}
          handleAddInterviewee={handleAddInterviewee}
          handleBulkAddOpen={handleBulkAddOpen}
          handleDeleteSelectedInterviewees={handleDeleteSelectedInterviewees}
          interviewees={interviewees}
          handleDeleteInterviewee={handleDeleteInterviewee}
          selectedInterviewees={selectedInterviewees}
          handleIntervieweeSelect={handleIntervieweeSelect}
          aiInstructions={aiInstructions}
          setAiInstructions={setAiInstructions}
          updateAiInstructions={updateAiInstructions}
          adminData={adminData}
          language={language}
          handleLanguageChange={handleLanguageChange}
          setCurrentView={setCurrentView}
          dashboardData={dashboardData}
        />
      </Box>
      <Box component="footer" className={styles.footer}>
        <Typography variant="body2" color="text.secondary">
          ©2025 aqili. All rights reserved.
        </Typography>
      </Box>
      <DialogBox
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        content="Are you sure you want to delete this interview report? This action cannot be undone."
        confirmText="Delete"
      />
      <DialogBox
        open={bulkAddOpen}
        onClose={handleBulkAddClose}
        onConfirm={handleBulkAddInterviewees}
        title="Bulk Add Interviewees"
        content={
          <>
            Enter multiple interviewees in the format: First Name, Last Name, Email. Each entry should be on a new line.
            <TextField
              fullWidth
              multiline
              rows={10}
              value={bulkInterviewees}
              onChange={(e) => setBulkInterviewees(e.target.value)}
              variant="outlined"
              margin="normal"
              placeholder="First Name, Last Name, Email"
            />
          </>
        }
        confirmText="Add"
      />
      <DialogBox
        open={deleteIntervieweeConfirmOpen}
        onClose={handleDeleteIntervieweeCancel}
        onConfirm={handleDeleteIntervieweeConfirm}
        title="Confirm Delete"
        content="Are you sure you want to delete the selected interviewees? This action cannot be undone."
        confirmText="Delete"
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
}

export default AdminDashboard;